import { useEffect, useState } from "react";
import {
  X,
  FileStack,
  FileText,
  Ticket,
  HeartPulse,
  ShoppingBag,
  Trophy,
  Dumbbell,
  Code,
  Globe,
  Smartphone,
  Briefcase,
  Camera,
  Music,
  Book,
  Star,
  Zap,
  Mail,
  Image,
  Video,
  ShoppingCart,
  Wallet,
  Calendar,
  Database,
  Cloud,
  Box,
} from "lucide-react";

const ICON_OPTIONS = [
  "FileStack", "FileText", "Ticket", "HeartPulse", "ShoppingBag", "Trophy", "Dumbbell",
  "Code", "Globe", "Smartphone", "Briefcase", "Camera", "Music", "Book", "Star",
  "Zap", "Mail", "Image", "Video", "ShoppingCart", "Wallet", "Calendar", "Database",
  "Cloud", "Box",
];

const ICON_MAP = {
  FileStack, FileText, Ticket, HeartPulse, ShoppingBag, Trophy, Dumbbell,
  Code, Globe, Smartphone, Briefcase, Camera, Music, Book, Star, Zap, Mail,
  Image, Video, ShoppingCart, Wallet, Calendar, Database, Cloud, Box,
};

const ACCENT_OPTIONS = [
  { value: "cyan", label: "Cyan", color: "hsl(var(--accent-cyan))" },
  { value: "green", label: "Green", color: "hsl(var(--accent-green))" },
  { value: "magenta", label: "Magenta", color: "hsl(var(--accent-magenta))" },
];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const empty = {
  slug: "",
  name: "",
  tagline: "",
  description: "",
  url: "",
  category: "",
  icon: "Box",
  accent: "cyan",
  featured: false,
  order: 0,
};

export default function AppForm({ initial, onClose, onSave, mode = "create" }) {
  const [form, setForm] = useState(initial || empty);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setForm(initial || empty);
    setErrors({});
  }, [initial]);

  const update = (k) => (e) => {
    const value = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: value };
      // Auto-generate slug from name when creating
      if (mode === "create" && k === "name" && !f.slug) {
        next.slug = slugify(value);
      }
      return next;
    });
  };

  const validate = () => {
    const e = {};
    if (!form.slug || !/^[a-z0-9-]+$/.test(form.slug))
      e.slug = "Solo minúsculas, números y guiones";
    if (!form.name?.trim()) e.name = "Requerido";
    if (!form.tagline?.trim()) e.tagline = "Requerido";
    if (!form.description?.trim()) e.description = "Requerido";
    if (!form.url?.trim() || !/^https?:\/\//.test(form.url))
      e.url = "Debe empezar con http:// o https://";
    if (!form.category?.trim()) e.category = "Requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setSubmitting(false);
    }
  };

  const Icon = ICON_MAP[form.icon] || Box;

  return (
    <div
      data-testid="app-form-overlay"
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        data-testid="app-form"
        onSubmit={handleSubmit}
        className="relative w-full max-w-2xl glass-strong p-6 md:p-8 my-8"
      >
        <button
          type="button"
          data-testid="app-form-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 grid place-items-center border border-border hover:border-foreground transition-colors"
          aria-label="Cerrar"
        >
          <X size={14} />
        </button>

        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent-cyan))] mb-2">
          {mode === "create" ? "// nueva_app" : "// editar_app"}
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight mb-6">
          {mode === "create" ? "Crear app" : `Editar: ${initial?.name}`}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Nombre" error={errors.name}>
            <input
              data-testid="form-name"
              type="text"
              value={form.name}
              onChange={update("name")}
              className={inputCls}
            />
          </Field>

          <Field label="Slug (URL interna)" error={errors.slug} hint="solo minúsculas, números y -">
            <input
              data-testid="form-slug"
              type="text"
              value={form.slug}
              onChange={update("slug")}
              disabled={mode === "edit"}
              className={inputCls + (mode === "edit" ? " opacity-50 cursor-not-allowed" : "")}
            />
          </Field>

          <Field label="Tagline (subtítulo)" error={errors.tagline} className="md:col-span-2">
            <input
              data-testid="form-tagline"
              type="text"
              value={form.tagline}
              onChange={update("tagline")}
              className={inputCls}
              maxLength={200}
            />
          </Field>

          <Field label="Descripción larga" error={errors.description} className="md:col-span-2">
            <textarea
              data-testid="form-description"
              value={form.description}
              onChange={update("description")}
              rows={3}
              className={inputCls + " resize-none"}
              maxLength={2000}
            />
          </Field>

          <Field label="URL" error={errors.url} className="md:col-span-2">
            <input
              data-testid="form-url"
              type="url"
              value={form.url}
              onChange={update("url")}
              placeholder="https://..."
              className={inputCls}
            />
          </Field>

          <Field label="Categoría" error={errors.category}>
            <input
              data-testid="form-category"
              type="text"
              value={form.category}
              onChange={update("category")}
              placeholder="ej: PDF Tools"
              className={inputCls}
              maxLength={60}
            />
          </Field>

          <Field label="Orden" hint="menor = aparece primero">
            <input
              data-testid="form-order"
              type="number"
              value={form.order}
              onChange={(e) =>
                setForm((f) => ({ ...f, order: parseInt(e.target.value) || 0 }))
              }
              className={inputCls}
            />
          </Field>

          <Field label="Ícono">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 grid place-items-center border border-border bg-card/50">
                <Icon size={20} strokeWidth={1.75} />
              </div>
              <select
                data-testid="form-icon"
                value={form.icon}
                onChange={update("icon")}
                className={inputCls + " py-2.5"}
              >
                {ICON_OPTIONS.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
          </Field>

          <Field label="Color de acento">
            <div className="flex gap-2">
              {ACCENT_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  data-testid={`form-accent-${opt.value}`}
                  onClick={() => setForm((f) => ({ ...f, accent: opt.value }))}
                  className={[
                    "flex-1 font-mono text-xs uppercase tracking-[0.15em] py-2.5 border transition-colors flex items-center justify-center gap-2",
                    form.accent === opt.value
                      ? "border-foreground"
                      : "border-border hover:border-foreground/60 text-muted-foreground",
                  ].join(" ")}
                >
                  <span
                    className="w-3 h-3 rounded-full"
                    style={{ background: opt.color }}
                  />
                  {opt.label}
                </button>
              ))}
            </div>
          </Field>

          <label className="md:col-span-2 inline-flex items-center gap-3 cursor-pointer mt-2">
            <input
              data-testid="form-featured"
              type="checkbox"
              checked={form.featured}
              onChange={update("featured")}
              className="w-4 h-4 accent-[hsl(var(--accent-green))]"
            />
            <span className="font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground">
              destacada (featured)
            </span>
          </label>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-8 pt-6 border-t border-border">
          <button
            type="button"
            data-testid="form-cancel"
            onClick={onClose}
            className="font-mono text-xs uppercase tracking-[0.15em] px-5 py-3 border border-border hover:border-foreground transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            data-testid="form-submit"
            disabled={submitting}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[hsl(var(--accent-green))] text-background font-mono font-bold uppercase tracking-[0.15em] text-xs px-6 py-3 border-2 border-[hsl(var(--accent-green))] hover:bg-transparent hover:text-[hsl(var(--accent-green))] disabled:opacity-50 transition-colors"
          >
            {submitting ? "guardando..." : mode === "create" ? "Crear app" : "Guardar cambios"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-cyan))] outline-none py-2 font-mono text-sm transition-colors";

const Field = ({ label, hint, error, children, className = "" }) => (
  <div className={className}>
    <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">
      {label}
      {hint && <span className="ml-2 normal-case tracking-normal text-muted-foreground/60">— {hint}</span>}
    </label>
    {children}
    {error && (
      <p className="mt-1 font-mono text-[11px] text-destructive">{error}</p>
    )}
  </div>
);
